import * as React from 'react'
import moment from 'moment'

import { ActiveMenuItem } from '@/app/constants/menuItem'
import { ReportType } from '@/app/constants/reportType'
import {
  getReportNumberByType,
  getReportNameByType,
  getReportTypeByNumber,
} from '@/app/services/printout/reportTypeResolver'
import { usePrintPdf } from '../hooks/usePrintPdf'
import { useBatchPrintout } from '../hooks/useBatchPrintout'
import { usePrintJobGuard } from '../hooks/usePrintJobGuard'
import { getDifferenceBetweenDatesInDays } from '@/app/common/dateHelper'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import PrintoutGroupsList from '../components/PrintoutGroupsList'
import { BatchGenerateBar } from '../components/BatchGenerateBar'
import { useSelectedReports } from '../stores/selectedReportsStore'
import {
  buildSubjectsKey,
  useGeneratedReports,
} from '../stores/generatedReportsStore'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { toast } from '@/lib/toasts/api'
import PrintoutParamsModal from '../components/PrintoutParamsModal'
import { useTaskTrackerStore } from '@/modules/shared/stores/taskTrackerStore'
import { usePrintoutSelectionStore } from '../stores/printoutSelectionStore'
import { useQueryVehicles } from '@/modules/exports/services/useQueryVehicles'
import { useShallow } from 'zustand/shallow'
import { TaskType } from '@/app/constants/taskType'
import { TableProvider } from '@/modules/drivers/context/TableContext'
import PrintoutDriversTable from '../components/PrintoutDriversTable'
import PrintoutVehiclesTable from '../components/PrintoutVehiclesTable'

import { RaportFormProvider, useRaportForm } from '../context/RaportFormContext'
import {
  AdminIcon,
  CollapsibleSidePanel,
  TaskStatusWrapper,
} from '@/modules/shared/components'
import { RouteComponentProps } from '@/app/navigation/withRouter'

type MatchParams = {
  companyId: string
  from?: string
  to?: string
}

type ReportItem = {
  name: string
  type: ReportType
  disabled: boolean
}

interface OwnProps extends RouteComponentProps<MatchParams> {}

type Props = OwnProps

const FavoritePrintoutsPageComponent: React.FC<Props> = ({ match }) => {
  const {
    checkedDrivers,
    checkedVehicles,
    setCheckedDrivers: dispatchSetCheckedDrivers,
    setCheckedVehicles: dispatchSetCheckedVehicles,
  } = usePrintoutSelectionStore(
    useShallow((state) => ({
      checkedDrivers: state.checkedDrivers,
      checkedVehicles: state.checkedVehicles,
      setCheckedDrivers: state.setCheckedDrivers,
      setCheckedVehicles: state.setCheckedVehicles,
    })),
  )
  const track = useTaskTrackerStore((state) => state.track)
  const { driverMode, dateRange, outputType } = useRaportForm()
  const { basicDrivers } = useDrivers()
  const { t } = useLocalize()
  const { params } = match
  const { companyId } = params

  // Lista pojazdow sluzy tylko do nazw folderow w paczce ZIP. Slice `vehicle` nikt juz
  // nie wypelnial, wiec byla pusta - stad puste nazwy przy wydrukach pojazdowych.
  const { data: vehicles = [] } = useQueryVehicles({ companyId })

  const { printPdf, loading, error: printError } = usePrintPdf()

  const dateFrom = React.useMemo(() => {
    const s = dateRange?.startDate
    if (s?.year != null && s?.month != null && s?.day != null) {
      return moment({ year: s.year, month: s.month - 1, day: s.day }).startOf(
        'day',
      )
    }
    return null
  }, [
    dateRange?.startDate?.year,
    dateRange?.startDate?.month,
    dateRange?.startDate?.day,
  ])

  const dateTo = React.useMemo(() => {
    const e = dateRange?.endDate
    if (e?.year != null && e?.month != null && e?.day != null) {
      return moment({ year: e.year, month: e.month - 1, day: e.day })
        .hour(23)
        .minute(59)
        .second(0)
        .millisecond(0)
    }
    return null
  }, [
    dateRange?.endDate?.year,
    dateRange?.endDate?.month,
    dateRange?.endDate?.day,
  ])
  const [selectedReport, setSelectedReport] = React.useState<ReportItem | null>(
    null,
  )
  const [showParamsModal, setShowParamsModal] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const { runPrintJob } = usePrintJobGuard()
  const [showDateRangeWarning, setShowDateRangeWarning] = React.useState(false)
  const isDriverMode = driverMode === ActiveMenuItem.PRINTOUT_DRIVER

  const {
    selectedReports,
    toggleReport,
    setReports,
    clearReports: clearReportSelection,
    syncCompany,
  } = useSelectedReports('favorites')

  const {
    generatedReports,
    lastGeneratedReports,
    markReportGenerated,
    syncGeneratedCompany,
    syncGeneratedSubjects,
  } = useGeneratedReports()

  // Zaznaczenie jest trwale (jak SelectedReportsConfiguration w WPF), ale nie
  // moze przeciekac miedzy firmami.
  React.useEffect(() => {
    syncCompany(companyId)
    syncGeneratedCompany(companyId)
  }, [companyId, syncCompany, syncGeneratedCompany])

  const subjectsKey = React.useMemo(
    () => buildSubjectsKey(checkedDrivers, checkedVehicles),
    [checkedDrivers, checkedVehicles],
  )

  React.useEffect(() => {
    syncGeneratedSubjects(subjectsKey)
  }, [subjectsKey, syncGeneratedSubjects])

  const selectedReportTypes = React.useMemo(
    () => new Set(selectedReports),
    [selectedReports],
  )

  const handleToggleReportSelection = React.useCallback(
    (report: ReportItem) => toggleReport(report.type),
    [toggleReport],
  )

  const handleToggleReportsSelection = React.useCallback(
    (reports: ReportItem[], select: boolean) =>
      setReports(
        reports.map((report) => report.type),
        select,
      ),
    [setReports],
  )

  const handleTaskCreated = React.useCallback(
    (taskId: string, reportType: ReportType) => {
      track({
        id: taskId,
        companyId,
        type: TaskType.Printout,
        data: reportType,
      })
    },
    [track, companyId],
  )

  const { generateReports, pending: batchPending } =
    useBatchPrintout(handleTaskCreated)

  const handleReportClick = (report: ReportItem) => {
    setSelectedReport(report)
    setShowParamsModal(true)
  }

  const handleDateRangeWarning = () => {
    setShowDateRangeWarning(true)
  }

  const handleModalClose = () => {
    setShowParamsModal(false)
    setSelectedReport(null)
  }
  const activeItem = driverMode
    ? ActiveMenuItem.PRINTOUT_DRIVER
    : ActiveMenuItem.PRINTOUT_VEHICLE

  const submitReportParams = async (params: any) => {
    if (!selectedReport || !dateFrom || !dateTo) {
      return
    }

    setIsSubmitting(true)
    const reportNumber = getReportNumberByType(selectedReport.type)


    try {
      const { taskId } = await printPdf({
        companyId,
        reportType: reportNumber,
        drivers:
          activeItem === ActiveMenuItem.PRINTOUT_DRIVER ? checkedDrivers : null,
        vehicles:
          activeItem === ActiveMenuItem.PRINTOUT_VEHICLE
            ? checkedVehicles
            : null,
        startTime: dateFrom,
        endTime: dateTo,
        outputType,
        data: params,
      })

      track({
        id: taskId,
        companyId,
        type: TaskType.Printout,
        data: getReportTypeByNumber(reportNumber),
      })

      markReportGenerated(selectedReport.type)

      toast.success({
        text:
          t('printout.start-generation-title') +
          ' ' +
          t('printout.start-generation-details').replace(
            '{0}',
            t(getReportNameByType(selectedReport.type)),
          ),
      })
    } catch (err: any) {
      const errorCode = err?.response?.status

      if (errorCode === 403) {
        toast.error({
          text: t('printout.processed-error'),
          autoCloseMs: 5000,
        })
      } else {
        toast.error({
          text: t('printout.processed-error'),
          autoCloseMs: 5000,
        })
      }

      console.error('Exception in printPdf:', err)
    } finally {
      setIsSubmitting(false)
    }

    handleModalClose()
  }

  const handleModalSubmit = (params: any) =>
    runPrintJob(() => submitReportParams(params))

  // Nazwy kierowcow/pojazdow ida do paczki ZIP jako foldery.
  const subjectNames = React.useMemo(() => {
    if (driverMode === ActiveMenuItem.PRINTOUT_DRIVER) {
      const ids = new Set(checkedDrivers.map((id) => parseInt(id, 10)))
      return basicDrivers
        .filter((driver) => ids.has(driver.id))
        .map((driver) => driver.fullName)
    }
    const ids = new Set(checkedVehicles.map(String))
    return (vehicles || [])
      .filter((vehicle) => ids.has(String(vehicle.id)))
      .map((vehicle) => vehicle.registrationNumber)
  }, [driverMode, checkedDrivers, checkedVehicles, basicDrivers, vehicles])

  const handleGenerateSelected = async () => {
    if (!dateFrom || !dateTo || selectedReportTypes.size === 0) return

    const rangeInDays = getDifferenceBetweenDatesInDays(dateFrom, dateTo)
    if (rangeInDays !== '-' && rangeInDays > 365) {
      setShowDateRangeWarning(true)
      return
    }

    // Bez zaznaczonych kierowcow/pojazdow raport nie ma dla kogo powstac.
    const hasSubjects =
      driverMode === ActiveMenuItem.PRINTOUT_DRIVER
        ? checkedDrivers.length > 0
        : checkedVehicles.length > 0
    const reports = hasSubjects ? Array.from(selectedReportTypes) : []

    if (reports.length === 0) {
      toast.error({
        text: t('printout.no-subjects-selected'),
        autoCloseMs: 4000,
      })
      return
    }

    await generateReports({
      companyId,
      reports,
      activeItem: driverMode,
      checkedDrivers: checkedDrivers || [],
      checkedVehicles: checkedVehicles || [],
      dateFrom,
      dateTo,
      outputType,
      subjectNames,
    })
  }

  const handleSendSelected = async () => {
    if (!dateFrom || !dateTo || selectedReportTypes.size === 0) return

    const rangeInDays = getDifferenceBetweenDatesInDays(dateFrom, dateTo)
    if (rangeInDays !== '-' && rangeInDays > 365) {
      setShowDateRangeWarning(true)
      return
    }

    const hasSubjects =
      driverMode === ActiveMenuItem.PRINTOUT_DRIVER
        ? checkedDrivers.length > 0
        : checkedVehicles.length > 0
    const reports = hasSubjects ? Array.from(selectedReportTypes) : []

    if (reports.length === 0) {
      toast.error({
        text: t('printout.no-subjects-selected'),
        autoCloseMs: 4000,
      })
      return
    }

    await generateReports({
      companyId,
      reports,
      activeItem: driverMode,
      checkedDrivers: checkedDrivers || [],
      checkedVehicles: checkedVehicles || [],
      dateFrom,
      dateTo,
      outputType,
      subjectNames,
      sendByEmail: true,
    })
  }

  const handleDriversSelectionChange = React.useCallback(
    (selectedIds: number[]) => {
      dispatchSetCheckedDrivers(selectedIds.map((id) => id.toString()))
    },
    [dispatchSetCheckedDrivers],
  )

  const handleVehiclesSelectionChange = React.useCallback(
    (selectedIds: string[]) => {
      dispatchSetCheckedVehicles(selectedIds)
    },
    [dispatchSetCheckedVehicles],
  )

  const memoizedDefaultSelectedDrivers = React.useMemo(
    () => checkedDrivers.map((id) => parseInt(id)).filter((id) => !isNaN(id)),
    [checkedDrivers],
  )

  const memoizedDefaultSelectedVehicles = React.useMemo(
    () => checkedVehicles.map((id) => parseInt(id)).filter((id) => !isNaN(id)),
    [checkedVehicles],
  )

  React.useEffect(() => {
    if (!dateFrom || !dateTo) {
      toast.error({
        text: t('printout.date-range-error'),
        autoCloseMs: 4000,
      })
    }
  }, [dateFrom, dateTo, t])

  if (!dateFrom || !dateTo) {
    return null
  }

  return (
    <TaskStatusWrapper>
      <section className="tw-relative tw-flex tw-min-h-0 tw-flex-1">
        {isDriverMode ? (
          <CollapsibleSidePanel
            className="tw-flex tw-min-h-0 tw-min-w-[350px] tw-flex-col tw-border-base-300"
            drawerClassName="tw-w-[350px] tw-max-w-[85vw]"
          >
            <TableProvider defaultSelected={memoizedDefaultSelectedDrivers}>
              <PrintoutDriversTable
                companyId={companyId}
                onSelectionChange={handleDriversSelectionChange}
                initialSelectedIds={memoizedDefaultSelectedDrivers}
              />
            </TableProvider>
          </CollapsibleSidePanel>
        ) : (
          <CollapsibleSidePanel
            className="tw-flex tw-min-h-0 tw-min-w-[350px] tw-flex-col tw-border-base-300"
            drawerClassName="tw-w-[350px] tw-max-w-[85vw]"
          >
            <TableProvider defaultSelected={memoizedDefaultSelectedVehicles}>
              <PrintoutVehiclesTable
                companyId={companyId}
                onSelectionChange={handleVehiclesSelectionChange}
              />
            </TableProvider>
          </CollapsibleSidePanel>
        )}

        {/* Favorite Printouts Groups Grid */}
        <div className="tw-flex tw-min-h-0 tw-w-full tw-min-w-0 tw-flex-col tw-overflow-x-auto">
          <div className="tw-flex tw-min-h-0 tw-min-w-[900px] tw-flex-1 tw-flex-col">
          <PrintoutGroupsList
            activeItem={driverMode}
            outputFormat={outputType}
            dateFrom={dateFrom}
            dateTo={dateTo}
            checkedDrivers={checkedDrivers || []}
            checkedVehicles={checkedVehicles || []}
            companyId={companyId}
            onReportClick={handleReportClick}
            onDateRangeWarning={handleDateRangeWarning}
            showOnlyFavorites={true}
            selectedReports={selectedReportTypes}
            onToggleReport={handleToggleReportSelection}
            onToggleReports={handleToggleReportsSelection}
            generatedReports={generatedReports}
            lastGeneratedReports={lastGeneratedReports}
          />
          <BatchGenerateBar
            count={selectedReportTypes.size}
            pending={batchPending}
            onGenerate={handleGenerateSelected}
            onSendEmail={handleSendSelected}
            onClear={clearReportSelection}
          />
          </div>
        </div>
        <RaportFormProvider key={selectedReport?.type}>
          {selectedReport && (
            <PrintoutParamsModal
              companyId={companyId}
              isOpen={showParamsModal}
              selectedReport={selectedReport.type}
              onClose={handleModalClose}
              onSubmit={handleModalSubmit}
              translate={t}
              pending={isSubmitting}
              dateRange={dateRange}
            />
          )}
        </RaportFormProvider>

        {/* Date Range Warning Modal */}
        {showDateRangeWarning && (
          <div
            className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center"
            onClick={() => setShowDateRangeWarning(false)}
          >
            <div className="tw-fixed tw-inset-0 tw-bg-black/50" />
            <div
              className="tw-relative tw-max-w-md tw-rounded-lg tw-bg-white tw-p-6 tw-shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="tw-mb-4 tw-flex tw-items-center tw-gap-5">
                <AdminIcon name="toast_warning" />
                <h3 className="tw-text-lg tw-font-semibold tw-text-content-base">
                  {t('printout.date-range-title')}
                </h3>
              </div>
              <p className="tw-mb-6 tw-text-sm tw-text-content-base">
                {t('printout.date-range-details')}
              </p>
              <div className="tw-flex tw-justify-end">
                <button
                  onClick={() => setShowDateRangeWarning(false)}
                  className="tw-rounded tw-bg-warning tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-transition-colors hover:tw-opacity-90"
                >
                  {t('answer.ok')}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </TaskStatusWrapper>
  )
}

export default FavoritePrintoutsPageComponent
export { FavoritePrintoutsPageComponent as FavoritePrintoutsPage }
